import type { GuessedMapping } from './types';

const KEY_ALIASES = [
  'part #',
  'part#',
  'sku',
  'item number',
  'order code',
  'catalog',
  'catalogue',
  'item',
] as const;

const BREAK_ALIASES = [
  'min qty',
  'qty break',
  'quantity break',
  'break qty',
  'qty',
  'quantity',
] as const;

const PRICE_ALIASES = [
  'distributor',
  'dist price',
  'net price',
  'net',
  'cost',
  'price',
] as const;

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function firstAliasMatch(
  headers: readonly string[],
  aliases: readonly string[],
): string | null {
  const byNorm = new Map<string, string>();
  for (const header of headers) {
    const norm = normalizeHeader(header);
    if (norm === '' || byNorm.has(norm)) continue;
    byNorm.set(norm, header);
  }
  for (const alias of aliases) {
    const found = byNorm.get(alias);
    if (found !== undefined) return found;
  }
  return null;
}

/** First matching alias for each role. More specific aliases win. */
export function guessMapping(headers: readonly string[]): GuessedMapping {
  return {
    keyColumn: firstAliasMatch(headers, KEY_ALIASES),
    breakColumn: firstAliasMatch(headers, BREAK_ALIASES),
    priceColumn: firstAliasMatch(headers, PRICE_ALIASES),
  };
}

export function isCompleteMapping(
  mapping: GuessedMapping,
): mapping is { keyColumn: string; breakColumn: string; priceColumn: string } {
  return (
    mapping.keyColumn !== null &&
    mapping.breakColumn !== null &&
    mapping.priceColumn !== null
  );
}

export function mappingUsesHeaders(
  mapping: GuessedMapping,
  headers: readonly string[],
): boolean {
  const chosen = [mapping.keyColumn, mapping.breakColumn, mapping.priceColumn];
  return chosen.every((column) => column === null || headers.includes(column));
}

export const EMPTY_MAPPING: GuessedMapping = {
  keyColumn: null,
  breakColumn: null,
  priceColumn: null,
};

/** Keep a complete mapping that still applies; otherwise guess from headers. */
export function nextMapping(
  current: GuessedMapping,
  headers: readonly string[],
): GuessedMapping {
  if (isCompleteMapping(current) && mappingUsesHeaders(current, headers)) {
    return current;
  }
  return guessMapping(headers);
}
